import React from "react";

import cn from "classnames";
import { Avatar, Box, IconButton, Tooltip, Typography } from "@mui/material";

import CustomIcon from "components/CustomIcon";
import icons from "enums/icons";

import useStyles from "../Chatbot.styles";
import { useIntl } from "react-intl";

interface ChatbotHeaderProps {
    isOpen: boolean;
    isOnline: boolean;
    isCompact: boolean;
    onCloseChatbox: (event: React.SyntheticEvent) => void;
    onOpenChatbox: (event: React.MouseEvent<HTMLDivElement>) => void;
    onMaximizeChatbox: (event: React.SyntheticEvent) => void;
    onCompactChatbox: (event: React.SyntheticEvent) => void;
}

const ChatbotHeader = ({
    isOpen,
    isOnline,
    isCompact,
    onCloseChatbox,
    onOpenChatbox,
    onMaximizeChatbox,
    onCompactChatbox,
}: ChatbotHeaderProps) => {
    const classes = useStyles();
    const intl = useIntl();

    return (
        <Box
            className={cn(classes.header, {
                [classes.headerClickable]: !isOpen,
                [classes.headerCompact]: isCompact,
            })}
            aria-label={!isOpen ? "Open Eddy chat" : undefined}
            onClick={onOpenChatbox}
        >
            <Box className={classes.headerContent}>
                <Avatar variant="rounded" className={classes.avatarContainerMedium} aria-hidden>
                    <CustomIcon icon={icons.eddyIcon} />
                </Avatar>

                <Box>
                    <Box display="flex" alignItems="center">
                        <Typography
                            className={cn(isOnline ? classes.title : classes.titleOffline, {
                                [classes.titleCompact]: isCompact,
                                [classes.titleOpenCompacted]: isOpen && isCompact,
                            })}
                        >
                            {intl.formatMessage({ id: "chatbot.header.title" })}
                        </Typography>
                        <Box className={classes.betaTagContainer}>
                            <Typography className={classes.betaTagText}>BETA</Typography>
                        </Box>
                    </Box>

                    <Box className={classes.statusRow}>
                        <Box
                            className={cn(classes.statusIndicator, {
                                [classes.statusIndicatorOffline]: !isOnline,
                            })}
                        />
                        <Typography
                            className={cn(classes.statusText, {
                                [classes.statusTextOffline]: !isOnline,
                            })}
                        >
                            {!isOnline
                                ? intl.formatMessage({ id: "chatbot.status.offline" })
                                : isOpen
                                ? intl.formatMessage({ id: "chatbot.status.chatOpen" })
                                : intl.formatMessage({ id: "chatbot.status.online" })}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {isOpen && (
                <Box className={classes.headerActions}>
                    <Tooltip
                        arrow
                        placement="bottom-end"
                        title={intl.formatMessage({ id: "chatbot.expandButton.tooltip" })}
                        classes={{ tooltip: classes.tooltip, arrow: classes.tooltipArrow }}
                    >
                        <IconButton
                            className={cn(classes.iconButton, {
                                [classes.maximizeButtonCompact]: isCompact,
                                [classes.iconButtonCompact]: isCompact,
                            })}
                            aria-label="Maximize chat window"
                            onClick={onMaximizeChatbox}
                        >
                            <CustomIcon icon={icons.maximizeIcon} />
                        </IconButton>
                    </Tooltip>

                    <Tooltip
                        arrow
                        placement="bottom-end"
                        title={intl.formatMessage({ id: "chatbot.sidePanelButton.tooltip" })}
                        classes={{ tooltip: classes.tooltip, arrow: classes.tooltipArrow }}
                    >
                        <IconButton
                            className={cn(classes.iconButton, classes.tabletButton, {
                                [classes.tabletButtonCompact]: isCompact,
                                [classes.iconButtonCompact]: isCompact,
                            })}
                            aria-label={
                                isCompact
                                    ? intl.formatMessage({ id: "chatbot.ariaLabel.expand" })
                                    : intl.formatMessage({ id: "chatbot.ariaLabel.compact" })
                            }
                            onClick={onCompactChatbox}
                        >
                            <CustomIcon icon={icons.tabletLaptop} />
                        </IconButton>
                    </Tooltip>

                    <IconButton
                        className={cn(classes.iconButton, classes.closeButton, {
                            [classes.closeButtonCompact]: isCompact,
                            [classes.iconButtonCompact]: isCompact,
                        })}
                        aria-label={intl.formatMessage({ id: "chatbot.ariaLabel.close" })}
                        onClick={onCloseChatbox}
                    >
                        <CustomIcon icon={icons.chatClose} />
                    </IconButton>
                </Box>
            )}
        </Box>
    );
};

export default ChatbotHeader;
